import { useLabels } from 'labels/hooks/useLabels/useLabels'
import {
	type ApplyArgs,
	FilterType,
	type RangePickerValue,
	type UseFiltersSlotItem,
	type UserFilter,
} from 'plp/types/Filters'
import { EventName } from 'plp/types/eventsObserver'
import { eventsObserver } from 'plp/utils/eventsObserver'
import { useEffect, useLayoutEffect, useState } from 'react'

import { useFiltersSearchParams } from '../useFiltersSearchParams'
import {
	ALL_PRICES,
	ORDER_METHODS,
	ORDER_OPTIONS,
	RECOMMENDED,
} from './constants'
import { disabledValuesOnSelectionChange } from './disabledValues'
import { filterItems } from './filterItems'
import { serialize } from './serializer/serializer'
import {
	clear,
	filteredItemsOnApply,
	getCurrentSelected,
	getHasSelection,
	normalizeRange,
	toggle,
	updateRangeLimit,
} from './utils'

export function useFilters(
	defaultFilters: UserFilter[],
	originalItems: UseFiltersSlotItem[],
	initialRange: RangePickerValue,
	preSelectedRange?: RangePickerValue,
	preSelectedOrder?: string
) {
	const { t } = useLabels()
	const { push } = useFiltersSearchParams()
	const [disabledValues, setDisabledValues] = useState<string[]>([])
	/**
	 * Selected by user
	 */
	const [selectedCount, setSelectedCount] = useState(originalItems.length)
	const [selectedFilters, setSelectedFilters] =
		useState<UserFilter[]>(defaultFilters)
	const [selectedOrder, setSelectedOrder] = useState<string>(
		preSelectedOrder ?? RECOMMENDED
	)

	const [selectedLimitRange, setSelectedLimitRange] = useState<
		RangePickerValue | undefined
	>(initialRange)
	const [selectedRange, setSelectedRange] = useState<
		RangePickerValue | undefined
	>(preSelectedRange ?? initialRange)
	/**
	 * Applied filters
	 */
	const [appliedFilters, setAppliedFilters] =
		useState<UserFilter[]>(defaultFilters)
	const [appliedOrder, setAppliedOrder] = useState<string>(
		preSelectedOrder ?? RECOMMENDED
	)
	const [appliedRange, setAppliedRange] = useState<
		RangePickerValue | undefined
	>(preSelectedRange ?? initialRange)
	/**
	 * Filtered items
	 */
	const [items, setItems] = useState(originalItems)

	function applySelection() {
		setAppliedOrder(selectedOrder)
		setAppliedFilters(selectedFilters)
		setAppliedRange(normalizeRange(selectedRange))
	}

	async function apply() {
		const serializedParams = serialize({
			selectedFilters,
			selectedOrder,
			selectedRange,
			selectedLimitRange,
		})
		push(serializedParams)
		eventsObserver.dispatch(EventName.APPLY_FILTERS, {
			selectedFilters,
			selectedOrder,
			selectedRange,
			selectedLimitRange,
			selectedCount,
		})
	}

	const selectedValues = {
		selectedOrder,
		selectedRange,
		selectedLimitRange,
	}

	const setters = {
		setSelectedRange,
		setSelectedLimitRange,
		setSelectedCount,
	}

	const updateCurrentRangeLimit = () =>
		updateRangeLimit(selectedFilters, originalItems, selectedValues, setters)

	useEffect(() => {
		const currentDisabledValues = disabledValuesOnSelectionChange(
			selectedFilters,
			originalItems,
			selectedRange,
			selectedLimitRange
		)
		updateCurrentRangeLimit()
		setDisabledValues(currentDisabledValues)
	}, [selectedFilters])

	useEffect(() => {
		const filteredItems = filterItems(
			selectedFilters,
			originalItems,
			selectedRange ?? ALL_PRICES,
			ORDER_METHODS[selectedOrder]
		)
		if (!filteredItems?.length) {
			eventsObserver.dispatch(EventName.ZERO_RESULTS)
		}
		setSelectedCount(filteredItems?.length ?? 0)
	}, [selectedRange])

	useEffect(() => {
		updateCurrentRangeLimit()
	}, [selectedOrder])

	useLayoutEffect(() => {
		if (appliedFilters || appliedRange || appliedOrder) {
			let filteredItems: UseFiltersSlotItem[] | undefined = []
			filteredItems = filteredItemsOnApply(
				originalItems,
				appliedFilters ?? [],
				appliedOrder,
				appliedRange
			)
			if (filteredItems?.length) {
				setItems(filteredItems)
				eventsObserver.dispatch<{
					selectedFilters: UserFilter[]
					items: UseFiltersSlotItem[]
				}>(EventName.APPLIED_FILTERS, {
					selectedFilters,
					items: filteredItems ?? [],
				})
			}
		}
	}, [appliedFilters, appliedOrder, appliedRange])

	useEffect(() => {
		const removeTags = (args: ApplyArgs) => {
			const current = getCurrentSelected(
				args,
				selectedFilters,
				selectedOrder,
				selectedRange,
				selectedLimitRange
			)
			const serializedParams = serialize(current)
			clear(
				selectedFilters,
				selectedLimitRange,
				setSelectedFilters,
				setSelectedOrder,
				setSelectedRange
			)
			push(serializedParams)
		}
		eventsObserver.subscribe<ApplyArgs>(EventName.DELETE_TAG, removeTags)
		return () => {
			eventsObserver.unsubscribe(removeTags)
		}
	}, [selectedRange, selectedOrder, selectedFilters])

	const orderFilter: UserFilter = {
		type: FilterType.order,
		label: 'product.filters.orderBy.title',
		selection: [selectedOrder],
		multipleChoice: false,
		options: ORDER_OPTIONS.map((option) => ({
			...option,
			label: t(option.label),
		})),
	}

	const hasSelection = getHasSelection(
		selectedFilters,
		selectedOrder,
		selectedRange,
		selectedLimitRange
	)

	return {
		items,
		selectedFilters,
		orderFilter,
		selectedOrder,
		selectedRange,
		selectedLimitRange,
		selectedCount,
		appliedFilters,
		appliedOrder,
		appliedRange,
		hasSelection,
		disabledValues,
		setSelectedOrder,
		applySelection,
		apply,
		clear: () => {
			clear(
				selectedFilters,
				selectedLimitRange,
				setSelectedFilters,
				setSelectedOrder,
				setSelectedRange
			)
			eventsObserver.dispatch(EventName.CLEAR_FILTERS)
		},
		setSelectedRange: (range: RangePickerValue) =>
			setSelectedRange(normalizeRange(range)),
		toggle: (type: string, value: string, id = '') => {
			toggle(type, value, id, selectedFilters, setSelectedFilters)
		},
	}
}
